// Integration panels — the live, interactive sections rendered by
// src/pages/Integrations.tsx: Gateway API keys, Webhooks, 3rd-party MQTT
// integrations, and the Testing panel (virtual devices + synthetic events).
// Extracted verbatim from the legacy pages/Developer.tsx when that page was
// dissolved; only the components still in use (and their helpers) were kept.
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import {
  getGatewaysByCompany,
  getApiKeys,
  createApiKey,
  revokeApiKey,
  updateApiKeyDevices,
  GatewayApiKey,
} from '../../services/gateways';
import { getDevices } from '../../services/devices';
import {
  getMqttIntegrations,
  createMqttIntegration,
  updateMqttIntegration,
  revokeMqttIntegration,
  getMqttIntegrationStatuses,
  MqttIntegration,
  MqttTopics,
} from '../../services/integrations';
import {
  getWebhooks,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  regenerateWebhookSecret,
  getWebhookDeliveries,
  Webhook,
  WebhookDelivery,
} from '../../services/webhooks';
import {
  getTestEventInfo,
  fireTestEvent,
  fireTestStateUpdate,
  publishVirtualSnapshots,
  getV2DeviceResource,
  getV2Devices,
  getV2DeviceCommands,
} from '../../services/testEvents';
import {
  SectionTitle,
  Button,
  FormGroup,
  Label,
  Input,
  Select,
  StatusChip,
  TableContainer,
  StyledTable,
  Th,
  Td,
  EmptyState,
  LoadingText,
  MutedText,
  InlineCode,
  AlertBanner,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  HelperText,
  LinkButton,
  Stack,
  Row,
  H2Title,
  Chevron,
} from '../../components/styled';
import { IconX } from '@tabler/icons-react';

// ─── Inline-style replacements — reusable utility wrappers ───────────────────
// Each one corresponds to a previously inline style={} pattern that appeared
// 2+ times in this file.

// 13px helper text below a section heading — replaces several
// <MutedText style={{ marginBottom: '1rem', fontSize: '13px', display: block }}>.
const SectionBlurb = styled(MutedText)`
  display: block;
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const SectionBlurbLg = styled(MutedText)`
  display: block;
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

// AlertBanner inside a modal body — gets a bottom margin to clear the
// form below it.
const ModalAlert = styled(AlertBanner)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

// Block-display InlineCode for showing topics/URLs in modals. Adds a
// padding box and breaks long strings.
const CodeBox = styled(InlineCode)`
  display: block;
  padding: ${({ theme }) => theme.spacing(2, 3)};
  word-break: break-all;
`;

// Tiny uppercase label. Used as a subheading inside expanded webhook
// delivery rows ("Error", "Response", "Request payload").
const MicroLabel = styled(MutedText)`
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Pre block for inlined JSON / response text inside expanded rows.
const CodePre = styled.pre`
  margin: 0;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: ${({ theme }) => theme.font.familyMono};
`;

// Error message line inside a delivery expand row.
const DeliveryErrorLine = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.color.red};
`;

// Td variants for nested expanded rows. Each maps to a previously inline
// padding override.
const ExpandedTdSm = styled.td`
  padding: ${({ theme }) => theme.spacing(3, 4)};
`;

const ExpandedDeliveriesTd = styled.td`
  padding: ${({ theme }) => theme.spacing(4, 6)};
  background: transparent;
`;

// Truncated cell with an explicit max-width.
const TruncatedTd = styled(Td)<{ $maxWidth: string }>`
  max-width: ${({ $maxWidth }) => $maxWidth};
  overflow: hidden;
  text-overflow: ellipsis;
`;

// "This cannot be undone" muted text below a delete confirmation.
const SmallMutedTop = styled(MutedText)`
  display: block;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

// Inline check-style label (event type filter, asActive checkbox, etc.).
const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  cursor: pointer;
`;

// Spaced span used inline next to a delivery timestamp.
// Wraps a category-label group inside the event-type picker.
const CategoryGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

// Wide ModalCard for the "Integration Ready" reveal modal that needs more
// horizontal space for the topic list.
const WideModalCard = styled(ModalCard)`
  width: 560px;
  max-width: 95vw;
`;

// Click-to-expand row in the deliveries table (cursor pointer + hover bg).
const ClickableTr = styled.tr`
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.background.tertiary};
  }
`;

// 12px muted text — used for the "{n} type(s)" hint in the webhooks list.
const SmallMuted = styled(MutedText)`
  font-size: 12px;
`;

// Block-display 13px muted text used in the secret-reveal modal.
const BlockMuted = styled(MutedText)`
  display: block;
  font-size: 13px;
`;

// Subheading inside expanded webhook delivery rows ("Recent Deliveries").
const DeliveriesTitle = styled(SectionTitle)`
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

// Wraps the wrapping flex of event-type checkboxes in the webhook form.
const EventTypeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

// "Generated key" reveal block — shows the new API key once with copy.
const KeyRevealBox = styled.div`
  background: ${({ theme }) => theme.background.secondary};
  border: 1px solid ${({ theme }) => theme.accent.primary};
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const KeyRevealLabel = styled.p`
  font-size: ${({ theme }) => theme.font.size.xs};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
`;

const KeyRevealValue = styled.code`
  display: block;
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: ${({ theme }) => theme.font.size.sm};
  word-break: break-all;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const KeyRevealWarning = styled.p`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.red};
  margin: 0;
`;

const EndpointList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.border.color.light};
  border-radius: ${({ theme }) => theme.border.radius.md};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const EndpointRow = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.border.color.light};
  &:last-child { border-bottom: none; }
`;

const EndpointHeader = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  padding: ${({ theme }) => theme.spacing(3, 4)};
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  &:hover { background: ${({ theme }) => theme.background.secondary}; }
`;

const ExpandCaret = styled.span<{ $open: boolean }>`
  font-size: 12px;
  color: ${({ theme }) => theme.font.color.tertiary};
  transform: ${({ $open }) => ($open ? 'rotate(90deg)' : 'rotate(0deg)')};
  transition: transform 120ms ease;
`;

const EndpointBody = styled.div`
  padding: ${({ theme }) => theme.spacing(3, 4, 4, 4)};
  background: ${({ theme }) => theme.background.secondary};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.secondary};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const JsonBlock = styled.pre`
  margin: 0;
  padding: ${({ theme }) => theme.spacing(2, 3)};
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-family: ${({ theme }) => theme.font.familyMono || 'monospace'};
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.font.color.primary};
  overflow-x: auto;
  white-space: pre;
`;

// ── Top-level collapsible section — matches the device-detail card format:
//    a flat white card with a clean header (title left, optional action +
//    rotating Tabler chevron right) that toggles the body.
const TopLevelDropdownWrapper = styled.section`
  margin-top: ${({ theme }) => theme.spacing(6)};
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.md};
  overflow: hidden;
`;

const TopLevelDropdownHeader = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  padding: ${({ theme }) => theme.spacing(4, 5)};
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  &:hover { background: ${({ theme }) => theme.background.tertiary}; }
`;

const TopLevelDropdownTitle = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.font.roles.sectionH2.size};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
`;

const TopLevelDropdownSubtitle = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.font.color.tertiary};
`;

const TopLevelDropdownBody = styled.div`
  padding: ${({ theme }) => theme.spacing(5, 5, 6)};
  border-top: 1px solid ${({ theme }) => theme.border.color.medium};
`;

export function TopLevelDropdown({
  title,
  subtitle,
  action,
  defaultOpen,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <TopLevelDropdownWrapper>
      <TopLevelDropdownHeader onClick={() => setOpen(!open)} aria-expanded={open}>
        <TopLevelDropdownTitle>{title}</TopLevelDropdownTitle>
        {subtitle && <TopLevelDropdownSubtitle>{subtitle}</TopLevelDropdownSubtitle>}
        {action && <span onClick={(e) => e.stopPropagation()}>{action}</span>}
        <Chevron open={open} />
      </TopLevelDropdownHeader>
      {open && <TopLevelDropdownBody>{children}</TopLevelDropdownBody>}
    </TopLevelDropdownWrapper>
  );
}

// Header label for accordion rows that aren't "method + path" shaped — used by
// the Virtual devices and How-it-works sections on the Testing tab where the
// header is a plain title rather than a request shape.
const AccordionTitle = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.font.color.primary};
`;

const SubsectionLabel = styled.h4`
  margin: ${({ theme }) => theme.spacing(0, 0, 2, 0)};
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.font.color.tertiary};
`;

// ─── API Keys section ────────────────────────────────────────────────────────

export function GatewayApiKeys({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [keyForm, setKeyForm] = useState({ name: '', expiresIn: '90d' });
  const [newKeyData, setNewKeyData] = useState<{ key: string } | null>(null);
  // Device-restriction picker: which key is being edited, and the working
  // selection ([] = whole fleet).
  const [deviceTarget, setDeviceTarget] = useState<GatewayApiKey | null>(null);
  const [devicePick, setDevicePick] = useState<string[]>([]);

  // The company fleet, for the picker (assigned devices only).
  const { data: fleetData } = useQuery({
    queryKey: ['dev-fleet-for-keys', companyId],
    queryFn: () => getDevices({ limit: 200, companyId }),
    enabled: !!deviceTarget,
  });
  const fleet = fleetData?.devices || [];

  const { data: gateways = [], isLoading: gatewaysLoading } = useQuery({
    queryKey: ['dev-gateways', companyId],
    queryFn: () => getGatewaysByCompany(companyId),
    refetchInterval: 30000,
  });

  // Single-gateway is the common case. Auto-target the first gateway returned.
  const gatewayId = gateways[0]?.id;
  const hasGateway = !!gatewayId;

  const { data: apiKeysData, isLoading: keysLoading } = useQuery({
    queryKey: ['dev-gateway-keys', gatewayId],
    queryFn: () => getApiKeys(gatewayId as any),
    enabled: hasGateway,
  });
  const apiKeys = apiKeysData?.keys || [];

  const createKeyMutation = useMutation({
    mutationFn: (data: any) => createApiKey(gatewayId as any, data),
    onSuccess: (data) => {
      setNewKeyData({ key: data.key });
      setShowCreateModal(false);
      setKeyForm({ name: '', expiresIn: '90d' });
      queryClient.invalidateQueries({ queryKey: ['dev-gateway-keys', gatewayId] });
    },
  });

  const revokeKeyMutation = useMutation({
    mutationFn: (keyId: number) => revokeApiKey(gatewayId as any, keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dev-gateway-keys', gatewayId] });
    },
  });

  const deviceScopeMutation = useMutation({
    mutationFn: ({ keyId, deviceIds }: { keyId: number; deviceIds: string[] }) =>
      updateApiKeyDevices(gatewayId as any, keyId, deviceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dev-gateway-keys', gatewayId] });
      setDeviceTarget(null);
    },
  });

  function openDevicePicker(k: GatewayApiKey) {
    setDeviceTarget(k);
    setDevicePick([...(k.device_ids || [])]);
  }

  function togglePicked(deviceId: string) {
    setDevicePick((cur) => cur.includes(deviceId) ? cur.filter((d) => d !== deviceId) : [...cur, deviceId]);
  }

  function handleRevoke(keyId: number) {
    if (!window.confirm('Revoke this API key? Applications using it will stop working immediately.')) return;
    revokeKeyMutation.mutate(keyId);
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    createKeyMutation.mutate(keyForm);
  }

  return (
    <TopLevelDropdown
      title="API Keys"
      defaultOpen
      action={
        <Button onClick={() => setShowCreateModal(true)} disabled={!hasGateway}>
          + New API Key
        </Button>
      }
    >

      <SectionBlurbLg>
        An API key is your application&apos;s password to Simkura&apos;s REST API. Send it as a
        <InlineCode> Bearer</InlineCode> token in the <InlineCode>Authorization</InlineCode> header
        on every request &mdash; to send commands to devices, fetch device state, manage credentials,
        or list your fleet. This is the <strong>outbound</strong> half of a REST integration:
        your app calling Simkura. Keys are shown once at creation; revoking takes effect immediately.
      </SectionBlurbLg>

      {newKeyData && (
        <KeyRevealBox>
          <KeyRevealLabel>New API Key Created</KeyRevealLabel>
          <KeyRevealValue>{newKeyData.key}</KeyRevealValue>
          <KeyRevealWarning>
            Copy this key now. It will not be shown again.
          </KeyRevealWarning>
        </KeyRevealBox>
      )}

      {gatewaysLoading || (hasGateway && keysLoading) ? (
        <LoadingText>Loading…</LoadingText>
      ) : !hasGateway ? (
        <EmptyState>
          <p>No gateway configured yet.</p>
          <MutedText>A gateway will be provisioned for your company automatically.</MutedText>
        </EmptyState>
      ) : apiKeys.length === 0 ? (
        <EmptyState>
          <p>No API keys yet.</p>
          <MutedText>Generate one to authenticate your application.</MutedText>
        </EmptyState>
      ) : (
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Prefix</Th>
                <Th>Status</Th>
                <Th>Devices</Th>
                <Th>Created</Th>
                <Th>Expires</Th>
                <Th>Last Used</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((k) => (
                <tr key={k.id}>
                  <Td>{k.name}</Td>
                  <Td><InlineCode>{k.key_prefix}…</InlineCode></Td>
                  <Td>
                    <StatusChip variant={k.is_active ? 'green' : 'gray'}>
                      {k.is_active ? 'Active' : 'Revoked'}
                    </StatusChip>
                  </Td>
                  <Td>
                    {k.is_active ? (
                      <LinkButton onClick={() => openDevicePicker(k)}>
                        {(k.device_ids?.length ?? 0) > 0 ? `${k.device_ids!.length} assigned` : 'All devices'}
                      </LinkButton>
                    ) : (
                      <MutedText>—</MutedText>
                    )}
                  </Td>
                  <Td>{new Date(k.created_at).toLocaleDateString()}</Td>
                  <Td>
                    {k.expires_at
                      ? new Date(k.expires_at).toLocaleDateString()
                      : <MutedText>Never</MutedText>}
                  </Td>
                  <Td>
                    {k.last_used_at
                      ? new Date(k.last_used_at).toLocaleString()
                      : <MutedText>Never</MutedText>}
                  </Td>
                  <Td>
                    {k.is_active ? (
                      <LinkButton
                        variant="danger"
                        onClick={() => handleRevoke(k.id)}
                        disabled={revokeKeyMutation.isPending}
                      >
                        Revoke
                      </LinkButton>
                    ) : (
                      <MutedText>—</MutedText>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableContainer>
      )}

      {deviceTarget && (
        <ModalOverlay onClick={() => setDeviceTarget(null)}>
          <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Devices — {deviceTarget.name}</H2Title>
              <ModalCloseButton onClick={() => setDeviceTarget(null)} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {deviceScopeMutation.isError && (
                <ModalAlert variant="error">
                  {(deviceScopeMutation.error as any)?.response?.data?.error || 'Failed to update device access'}
                </ModalAlert>
              )}
              <SectionBlurbLg>
                Choose which devices this key can read and command. With none
                selected the key covers your <strong>entire fleet</strong>,
                including devices assigned to you later.
              </SectionBlurbLg>
              {fleet.length === 0 ? (
                <EmptyState><p>No devices assigned to your organization yet.</p></EmptyState>
              ) : (
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {fleet.map((d) => (
                    <CheckLabel key={d.device_id} style={{ display: 'flex', padding: '4px 0' }}>
                      <input
                        type="checkbox"
                        checked={devicePick.includes(d.device_id)}
                        onChange={() => togglePicked(d.device_id)}
                      />
                      {d.name || d.device_id}
                      {d.name && <MutedText>&nbsp;·&nbsp;{d.device_id}</MutedText>}
                    </CheckLabel>
                  ))}
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" type="button" onClick={() => setDevicePick([])}>
                Clear (all devices)
              </Button>
              <Button
                type="button"
                onClick={() => deviceScopeMutation.mutate({ keyId: deviceTarget.id, deviceIds: devicePick })}
                disabled={deviceScopeMutation.isPending}
              >
                {deviceScopeMutation.isPending
                  ? 'Saving…'
                  : devicePick.length > 0 ? `Restrict to ${devicePick.length} device${devicePick.length > 1 ? 's' : ''}` : 'Allow all devices'}
              </Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      {showCreateModal && (
        <ModalOverlay onClick={() => setShowCreateModal(false)}>
          <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Generate API Key</H2Title>
              <ModalCloseButton onClick={() => setShowCreateModal(false)} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
            </ModalHeader>
            <form onSubmit={handleCreate}>
              <ModalBody>
                {createKeyMutation.isError && (
                  <ModalAlert variant="error">
                    {(createKeyMutation.error as any)?.response?.data?.error || 'Failed to create API key'}
                  </ModalAlert>
                )}
                <FormGroup>
                  <Label>Key Name</Label>
                  <Input
                    value={keyForm.name}
                    onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })}
                    placeholder="e.g., Production Integration"
                    required
                  />
                  <HelperText>A descriptive name for this key</HelperText>
                </FormGroup>
                <FormGroup>
                  <Label>Expiration</Label>
                  <Select
                    value={keyForm.expiresIn}
                    onChange={(e) => setKeyForm({ ...keyForm, expiresIn: e.target.value })}
                  >
                    <option value="30d">30 days</option>
                    <option value="90d">90 days</option>
                    <option value="180d">180 days</option>
                    <option value="365d">1 year</option>
                    <option value="">Never</option>
                  </Select>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createKeyMutation.isPending}>
                  {createKeyMutation.isPending ? 'Creating…' : 'Generate Key'}
                </Button>
              </ModalFooter>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
    </TopLevelDropdown>
  );
}

// ─── MQTT Integrations section ───────────────────────────────────────────────

function integrationConnVariant(c?: { connected: boolean; reconnecting: boolean; error: string | null }): 'green' | 'red' | 'yellow' | 'gray' {
  if (!c) return 'gray';
  if (c.error) return 'red';
  if (c.connected) return 'green';
  if (c.reconnecting) return 'yellow';
  return 'gray';
}

function integrationConnLabel(c?: { connected: boolean; reconnecting: boolean; error: string | null }): string {
  if (!c) return 'Unknown';
  if (c.error === 'auth_rejected') return 'Auth error';
  if (c.error === 'circuit_open') return 'Offline (retrying)';
  if (c.error) return 'Error';
  if (c.connected) return 'Connected';
  if (c.reconnecting) return 'Reconnecting';
  return 'Offline';
}

export function MqttIntegrations({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [showNewModal, setShowNewModal] = useState(false);
  const [form, setForm] = useState({ appName: '', teamId: '', apiKey: '' });
  const [newTopics, setNewTopics] = useState<{ topics: MqttTopics; appName: string } | null>(null);
  const [editTarget, setEditTarget] = useState<MqttIntegration | null>(null);
  const [editForm, setEditForm] = useState({ teamId: '', apiKey: '' });
  const [revokeTarget, setRevokeTarget] = useState<MqttIntegration | null>(null);

  const { data: integrations = [], isLoading, error } = useQuery({
    queryKey: ['dev-mqtt-integrations', companyId],
    queryFn: () => getMqttIntegrations(companyId),
  });

  const { data: statuses = {} } = useQuery({
    queryKey: ['dev-mqtt-statuses', companyId],
    queryFn: () => getMqttIntegrationStatuses(companyId),
    enabled: integrations.length > 0,
    refetchInterval: 10000,
  });

  const createMutation = useMutation({
    mutationFn: () => createMqttIntegration(companyId, form.appName.trim(), form.teamId.trim(), form.apiKey.trim()),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dev-mqtt-integrations', companyId] });
      setShowNewModal(false);
      setForm({ appName: '', teamId: '', apiKey: '' });
      setNewTopics({ topics: data.topics, appName: data.integration.app_name });
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => updateMqttIntegration(companyId, editTarget!.id, editForm.teamId.trim(), editForm.apiKey.trim()),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dev-mqtt-integrations', companyId] });
      setEditTarget(null);
      setEditForm({ teamId: '', apiKey: '' });
      setNewTopics({ topics: data.topics, appName: data.integration.app_name });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: number) => revokeMqttIntegration(companyId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dev-mqtt-integrations', companyId] });
      setRevokeTarget(null);
    },
  });

  function closeNewModal() {
    setShowNewModal(false);
    createMutation.reset();
    setForm({ appName: '', teamId: '', apiKey: '' });
  }
  function closeEditModal() {
    setEditTarget(null);
    updateMutation.reset();
    setEditForm({ teamId: '', apiKey: '' });
  }

  return (
    <TopLevelDropdown
      title="3rd Party App Integrations"
      defaultOpen
      action={<Button onClick={() => setShowNewModal(true)}>+ New Integration</Button>}
    >

      <SectionBlurbLg>
        Each integration provisions a bridge client on the 3rd party&apos;s own nRF Cloud team,
        letting their app send commands and receive device events without sharing your
        nRF Cloud credentials. Applies to all devices in your company — any gateway.
      </SectionBlurbLg>

      {isLoading && <LoadingText>Loading integrations…</LoadingText>}
      {error && <AlertBanner variant="error">Failed to load integrations.</AlertBanner>}

      {!isLoading && !error && integrations.length === 0 && (
        <EmptyState>
          <p>No 3rd party app integrations configured.</p>
        </EmptyState>
      )}

      {integrations.length > 0 && (
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <Th>App Name</Th>
                <Th>Team ID</Th>
                <Th>Bridge Client ID</Th>
                <Th>Status</Th>
                <Th>Connection</Th>
                <Th>Created</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {integrations.map((it) => {
                const c = statuses[it.id];
                return (
                  <tr key={it.id}>
                    <Td>{it.app_name}</Td>
                    <Td><InlineCode>{it.third_party_team_id}</InlineCode></Td>
                    <Td><InlineCode>{it.bridge_client_id}</InlineCode></Td>
                    <Td>
                      <StatusChip variant={it.status === 'active' ? 'green' : 'gray'}>
                        {it.status}
                      </StatusChip>
                    </Td>
                    <Td>
                      {it.status === 'active' && (
                        <StatusChip variant={integrationConnVariant(c)}>
                          {integrationConnLabel(c)}
                        </StatusChip>
                      )}
                    </Td>
                    <Td>{new Date(it.created_at).toLocaleDateString()}</Td>
                    <Td>
                      {it.status === 'active' && (
                        <Row gap={2}>
                          <LinkButton
                            onClick={() => {
                              setEditTarget(it);
                              setEditForm({ teamId: it.third_party_team_id, apiKey: '' });
                            }}
                          >
                            Edit
                          </LinkButton>
                          <LinkButton variant="danger" onClick={() => setRevokeTarget(it)}>
                            Revoke
                          </LinkButton>
                        </Row>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </TableContainer>
      )}

      {/* New integration modal */}
      {showNewModal && (
        <ModalOverlay onClick={closeNewModal}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>New 3rd Party App Integration</H2Title>
              <ModalCloseButton onClick={closeNewModal} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {createMutation.error && (
                <ModalAlert variant="error">
                  {(createMutation.error as any)?.response?.data?.error || 'Failed to provision integration.'}
                </ModalAlert>
              )}
              <FormGroup>
                <Label>App Name</Label>
                <Input
                  value={form.appName}
                  onChange={(e) => setForm(f => ({ ...f, appName: e.target.value }))}
                  placeholder="e.g. Building Management System"
                  autoFocus
                />
                <HelperText>A label to identify this integration.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label>nRF Cloud Team ID</Label>
                <Input
                  value={form.teamId}
                  onChange={(e) => setForm(f => ({ ...f, teamId: e.target.value }))}
                  placeholder="3rd party's nRF Cloud team ID"
                />
                <HelperText>The 3rd party app&apos;s own nRF Cloud team identifier.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label>nRF Cloud API Key</Label>
                <Input
                  type="password"
                  value={form.apiKey}
                  onChange={(e) => setForm(f => ({ ...f, apiKey: e.target.value }))}
                  placeholder="3rd party's nRF Cloud API key"
                />
                <HelperText>Used once to provision the bridge client. Stored securely — not returned.</HelperText>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={closeNewModal}>Cancel</Button>
              <Button
                onClick={() => createMutation.mutate()}
                disabled={!form.appName.trim() || !form.teamId.trim() || !form.apiKey.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? 'Provisioning…' : 'Provision'}
              </Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Topics reveal — shown once after provisioning */}
      {newTopics && (
        <ModalOverlay>
          <WideModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Integration Ready — {newTopics.appName}</H2Title>
              <ModalCloseButton onClick={() => setNewTopics(null)} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <SectionBlurb>
                Share these topic patterns with the 3rd party. Replace <InlineCode>{'{deviceId}'}</InlineCode> with
                the nRF Cloud device ID of the target device.
              </SectionBlurb>
              <Stack gap={3}>
                <FormGroup>
                  <Label>Command Topic</Label>
                  <CodeBox>
                    {newTopics.topics.commandTopic}
                  </CodeBox>
                </FormGroup>
                <FormGroup>
                  <Label>Events Topic</Label>
                  <CodeBox>
                    {newTopics.topics.eventsTopic}
                  </CodeBox>
                </FormGroup>
                <FormGroup>
                  <Label>State Topic <MutedText as="span">(current state snapshot, published on change)</MutedText></Label>
                  <CodeBox>
                    {newTopics.topics.stateTopic}
                  </CodeBox>
                </FormGroup>
              </Stack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setNewTopics(null)}>Done</Button>
            </ModalFooter>
          </WideModalCard>
        </ModalOverlay>
      )}

      {/* Edit integration modal */}
      {editTarget && (
        <ModalOverlay onClick={closeEditModal}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Edit Integration — {editTarget.app_name}</H2Title>
              <ModalCloseButton onClick={closeEditModal} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {updateMutation.error && (
                <ModalAlert variant="error">
                  {(updateMutation.error as any)?.response?.data?.error || 'Failed to update integration.'}
                </ModalAlert>
              )}
              <FormGroup>
                <Label>nRF Cloud Team ID</Label>
                <Input
                  value={editForm.teamId}
                  onChange={(e) => setEditForm(f => ({ ...f, teamId: e.target.value }))}
                  placeholder="3rd party's nRF Cloud team ID"
                  autoFocus
                />
              </FormGroup>
              <FormGroup>
                <Label>nRF Cloud API Key</Label>
                <Input
                  type="password"
                  value={editForm.apiKey}
                  onChange={(e) => setEditForm(f => ({ ...f, apiKey: e.target.value }))}
                  placeholder="3rd party's nRF Cloud API key"
                />
                <HelperText>A new bridge client will be provisioned. The old connection closes immediately.</HelperText>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={closeEditModal}>Cancel</Button>
              <Button
                onClick={() => updateMutation.mutate()}
                disabled={!editForm.teamId.trim() || !editForm.apiKey.trim() || updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Reprovisioning…' : 'Save'}
              </Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Revoke confirmation modal */}
      {revokeTarget && (
        <ModalOverlay onClick={() => setRevokeTarget(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Revoke Integration</H2Title>
              <ModalCloseButton onClick={() => setRevokeTarget(null)} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {revokeMutation.error && (
                <ModalAlert variant="error">
                  Failed to revoke integration.
                </ModalAlert>
              )}
              <p>
                Revoke <strong>{revokeTarget.app_name}</strong>? The MQTT bridge disconnects
                immediately and the 3rd party app stops receiving events for your devices.
              </p>
              <SmallMutedTop>This cannot be undone.</SmallMutedTop>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setRevokeTarget(null)}>Cancel</Button>
              <Button
                variant="danger"
                onClick={() => revokeMutation.mutate(revokeTarget.id)}
                disabled={revokeMutation.isPending}
              >
                {revokeMutation.isPending ? 'Revoking…' : 'Revoke'}
              </Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}
    </TopLevelDropdown>
  );
}

// ─── Webhooks section ────────────────────────────────────────────────────────

// The full delivered vocabulary — keep in sync with WEBHOOK_EVENT_TYPES in
// shared/constants.js (and the docs event catalog). An event type missing
// here can still be delivered (empty filter = everything) but can't be
// selectively subscribed from the app.
const WEBHOOK_EVENT_TYPES: Array<{ value: string; label: string; category: string }> = [
  { value: 'access.granted',            label: 'Access granted',            category: 'Access' },
  { value: 'access.denied',             label: 'Access denied',             category: 'Access' },
  { value: 'lock.state_changed',        label: 'Lock state changed',        category: 'Lock' },
  { value: 'command.sent',              label: 'Command sent',              category: 'Command' },
  { value: 'command.failed',            label: 'Command failed',            category: 'Command' },
  { value: 'device.wake',               label: 'Device wake',               category: 'Device' },
  { value: 'device.sleep',              label: 'Device sleep',              category: 'Device' },
  { value: 'device.restart',            label: 'Device restart',            category: 'Device' },
  { value: 'device.reconnect',          label: 'Device reconnect',          category: 'Device' },
  { value: 'device.offline',            label: 'Device offline',            category: 'Device' },
  { value: 'device.online',             label: 'Device online',             category: 'Device' },
  { value: 'device.deployed',           label: 'Device deployed',           category: 'Device' },
  { value: 'device.undeployed',         label: 'Device undeployed',         category: 'Device' },
  { value: 'health.battery_low',        label: 'Battery low',               category: 'Health' },
  { value: 'health.battery_dead',       label: 'Battery dead (safe mode)',  category: 'Health' },
  { value: 'health.battery_recovered',  label: 'Battery recovered',         category: 'Health' },
  { value: 'health.reader_wedged',      label: 'Reader wedged',             category: 'Health' },
  { value: 'health.recovery_boot',      label: 'Crash-recovery boot',       category: 'Health' },
  { value: 'webhook.disabled',          label: 'Webhook auto-disabled',     category: 'Webhook' },
];

type WebhookForm = {
  name: string;
  url: string;
  description: string;
  event_types: string[];
  is_active: boolean;
  payload_version: 'v1' | 'v2';
};

const BLANK_WEBHOOK_FORM: WebhookForm = {
  name: '',
  url: '',
  description: '',
  event_types: [],
  is_active: true,
  // New webhooks get the current delivery contract; v1 is the frozen legacy
  // envelope for pre-existing subscribers.
  payload_version: 'v2',
};

function EventTypesPicker({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  const categories = Array.from(new Set(WEBHOOK_EVENT_TYPES.map(e => e.category)));
  function toggle(v: string) {
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  }
  return (
    <div>
      {categories.map(cat => (
        <CategoryGroup key={cat}>
          <MicroLabel>
            {cat}
          </MicroLabel>
          <EventTypeRow>
            {WEBHOOK_EVENT_TYPES.filter(e => e.category === cat).map(e => (
              <CheckLabel key={e.value}>
                <input
                  type="checkbox"
                  checked={selected.includes(e.value)}
                  onChange={() => toggle(e.value)}
                />
                {e.label}
              </CheckLabel>
            ))}
          </EventTypeRow>
        </CategoryGroup>
      ))}
    </div>
  );
}

function deliveryStatusVariant(s: string): 'positive' | 'critical' | 'warning' | 'neutral' {
  if (s === 'success') return 'positive';
  if (s === 'failed') return 'critical';
  if (s === 'pending') return 'warning';
  return 'neutral';
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function Webhooks({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [showNewModal, setShowNewModal] = useState(false);
  const [form, setForm] = useState<WebhookForm>(BLANK_WEBHOOK_FORM);
  const [editTarget, setEditTarget] = useState<Webhook | null>(null);
  const [editForm, setEditForm] = useState<WebhookForm>(BLANK_WEBHOOK_FORM);
  const [deleteTarget, setDeleteTarget] = useState<Webhook | null>(null);
  const [regenTarget, setRegenTarget] = useState<Webhook | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<{ name: string; secret: string } | null>(null);
  const [expandedDeliveriesId, setExpandedDeliveriesId] = useState<string | null>(null);
  const [expandedDeliveryId, setExpandedDeliveryId] = useState<string | null>(null);
  const { data: webhooks = [], isLoading, error } = useQuery({
    queryKey: ['dev-webhooks', companyId],
    queryFn: () => getWebhooks(companyId),
  });

  const { data: deliveriesData, isLoading: deliveriesLoading } = useQuery({
    queryKey: ['dev-webhook-deliveries', expandedDeliveriesId],
    queryFn: () => getWebhookDeliveries(expandedDeliveriesId!, { limit: 20 }),
    enabled: !!expandedDeliveriesId,
    refetchInterval: 15000,
  });
  const deliveries: WebhookDelivery[] = deliveriesData?.deliveries || [];

  const createMutation = useMutation({
    mutationFn: () => createWebhook({
      name: form.name.trim(),
      url: form.url.trim(),
      description: form.description.trim() || undefined,
      event_types: form.event_types,
      payload_version: form.payload_version,
    }, companyId),
    onSuccess: (webhook) => {
      queryClient.invalidateQueries({ queryKey: ['dev-webhooks', companyId] });
      setShowNewModal(false);
      setForm(BLANK_WEBHOOK_FORM);
      if (webhook.secret) {
        setRevealedSecret({ name: webhook.name, secret: webhook.secret });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => updateWebhook(editTarget!.id, {
      name: editForm.name.trim(),
      url: editForm.url.trim(),
      description: editForm.description.trim() || undefined,
      event_types: editForm.event_types,
      is_active: editForm.is_active,
      payload_version: editForm.payload_version,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dev-webhooks', companyId] });
      setEditTarget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteWebhook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dev-webhooks', companyId] });
      setDeleteTarget(null);
    },
  });

  const regenMutation = useMutation({
    mutationFn: (id: string) => regenerateWebhookSecret(id),
    onSuccess: (data) => {
      if (regenTarget) {
        setRevealedSecret({ name: regenTarget.name, secret: data.secret });
      }
      setRegenTarget(null);
    },
  });

  function openEdit(w: Webhook) {
    setEditTarget(w);
    setEditForm({
      name: w.name,
      url: w.url,
      description: w.description || '',
      event_types: w.event_types || [],
      is_active: w.is_active,
      // Pre-payload-version rows have no column value in older responses.
      payload_version: w.payload_version || 'v1',
    });
  }

  function closeNew() {
    setShowNewModal(false);
    createMutation.reset();
    setForm(BLANK_WEBHOOK_FORM);
  }

  function closeEdit() {
    setEditTarget(null);
    updateMutation.reset();
  }

  return (
    <TopLevelDropdown
      title="Webhooks"
      defaultOpen
      action={<Button onClick={() => setShowNewModal(true)}>+ New Webhook</Button>}
    >

      <SectionBlurbLg>
        A webhook is a URL Simkura calls when something happens on your devices. Register an HTTPS
        endpoint, choose which event types matter to you (access granted, lock state changes,
        command lifecycle, device wake/sleep), and we&apos;ll POST each event there in real time &mdash;
        signed with an HMAC secret so you can verify it came from us. This is the
        <strong> inbound</strong> half of a REST integration: Simkura calling your app. No polling
        required.
      </SectionBlurbLg>

      {isLoading && <LoadingText>Loading webhooks…</LoadingText>}
      {error && <AlertBanner variant="error">Failed to load webhooks.</AlertBanner>}

      {!isLoading && !error && webhooks.length === 0 && (
        <EmptyState>
          <p>No webhooks configured.</p>
          <MutedText>Create a webhook to start receiving device events at your endpoint.</MutedText>
        </EmptyState>
      )}

      {webhooks.length > 0 && (
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>URL</Th>
                <Th>Events</Th>
                <Th>Active</Th>
                <Th>Sent / Failed</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {webhooks.map((w) => {
                const isExpanded = expandedDeliveriesId === w.id;
                return (
                  <React.Fragment key={w.id}>
                    <tr>
                      <Td>{w.name}</Td>
                      <TruncatedTd $maxWidth="240px">
                        <InlineCode title={w.url}>{w.url}</InlineCode>
                      </TruncatedTd>
                      <Td>
                        {w.event_types?.length
                          ? <SmallMuted>{w.event_types.length} type{w.event_types.length > 1 ? 's' : ''}</SmallMuted>
                          : <MutedText>All</MutedText>}
                      </Td>
                      <Td>
                        <Row gap={1} wrap="wrap">
                          <StatusChip
                            variant={w.is_active ? 'positive' : (w.disabled_reason ? 'critical' : 'neutral')}
                            title={w.disabled_reason
                              ? `Disabled automatically: ${w.disabled_reason}. Fix the endpoint, then re-enable from Edit.`
                              : undefined}
                          >
                            {w.is_active ? 'Active' : (w.disabled_reason ? 'Auto-disabled' : 'Paused')}
                          </StatusChip>
                          <StatusChip
                            variant={(w.payload_version || 'v1') === 'v2' ? 'positive' : 'neutral'}
                            title={(w.payload_version || 'v1') === 'v2'
                              ? 'Delivers the current v2 payload format'
                              : 'Delivers the frozen legacy v1 payload format'}
                          >
                            {w.payload_version || 'v1'}
                          </StatusChip>
                        </Row>
                      </Td>
                      <Td>
                        {w.stats
                          ? `${w.stats.successful} / ${w.stats.failed}`
                          : <MutedText>—</MutedText>}
                      </Td>
                      <Td>
                        <Row gap={2} wrap="wrap">
                          <LinkButton
                            onClick={() => {
                              setExpandedDeliveriesId(isExpanded ? null : w.id);
                              setExpandedDeliveryId(null);
                            }}
                          >
                            {isExpanded ? 'Hide deliveries' : 'Deliveries'}
                          </LinkButton>
                          <LinkButton onClick={() => openEdit(w)}>Edit</LinkButton>
                          <LinkButton onClick={() => setRegenTarget(w)}>Regen Secret</LinkButton>
                          <LinkButton variant="danger" onClick={() => setDeleteTarget(w)}>Delete</LinkButton>
                        </Row>
                      </Td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <ExpandedDeliveriesTd colSpan={6}>
                          <DeliveriesTitle>
                            Recent deliveries — {w.name}
                          </DeliveriesTitle>
                          {deliveriesLoading ? (
                            <LoadingText>Loading deliveries…</LoadingText>
                          ) : deliveries.length === 0 ? (
                            <EmptyState>
                              <p>No deliveries yet.</p>
                              <MutedText>Wait for an event to trigger delivery, or fire one from the test panel.</MutedText>
                            </EmptyState>
                          ) : (
                            <TableContainer>
                              <StyledTable>
                                <thead>
                                  <tr>
                                    <Th>Time</Th>
                                    <Th>Event</Th>
                                    <Th>Status</Th>
                                    <Th>HTTP</Th>
                                    <Th>Duration</Th>
                                    <Th>Attempts</Th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {deliveries.map(d => {
                                    const open = expandedDeliveryId === d.id;
                                    return (
                                      <React.Fragment key={d.id}>
                                        <ClickableTr
                                          onClick={() => setExpandedDeliveryId(open ? null : d.id)}
                                        >
                                          <Td title={new Date(d.created_at).toLocaleString()}>
                                            {timeAgo(d.created_at)}
                                          </Td>
                                          <Td>
                                            {d.event_type || <MutedText>—</MutedText>}
                                          </Td>
                                          <Td>
                                            <StatusChip variant={deliveryStatusVariant(d.status)}>
                                              {d.status}
                                            </StatusChip>
                                          </Td>
                                          <Td>{d.http_status ?? <MutedText>—</MutedText>}</Td>
                                          <Td>{d.duration_ms != null ? `${d.duration_ms}ms` : <MutedText>—</MutedText>}</Td>
                                          <Td>{d.attempts ?? <MutedText>—</MutedText>}</Td>
                                        </ClickableTr>
                                        {open && (
                                          <tr>
                                            <ExpandedTdSm colSpan={6}>
                                              <Stack gap={2}>
                                                {d.error_message && (
                                                  <div>
                                                    <MicroLabel>Error</MicroLabel>
                                                    <DeliveryErrorLine>{d.error_message}</DeliveryErrorLine>
                                                  </div>
                                                )}
                                                {d.response_body && (
                                                  <div>
                                                    <MicroLabel>Response</MicroLabel>
                                                    <CodePre>
                                                      {d.response_body.slice(0, 1000)}{d.response_body.length > 1000 ? '…' : ''}
                                                    </CodePre>
                                                  </div>
                                                )}
                                                {d.request_payload && (
                                                  <div>
                                                    <MicroLabel>Request payload</MicroLabel>
                                                    <CodePre>
                                                      {JSON.stringify(d.request_payload, null, 2)}
                                                    </CodePre>
                                                  </div>
                                                )}
                                              </Stack>
                                            </ExpandedTdSm>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </StyledTable>
                            </TableContainer>
                          )}
                        </ExpandedDeliveriesTd>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </StyledTable>
        </TableContainer>
      )}

      {/* New webhook modal */}
      {showNewModal && (
        <ModalOverlay onClick={closeNew}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>New Webhook</H2Title>
              <ModalCloseButton onClick={closeNew} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {createMutation.error && (
                <ModalAlert variant="error">
                  {(createMutation.error as any)?.response?.data?.error || 'Failed to create webhook.'}
                </ModalAlert>
              )}
              <FormGroup>
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Production event sink"
                  autoFocus
                />
              </FormGroup>
              <FormGroup>
                <Label>URL</Label>
                <Input
                  type="url"
                  value={form.url}
                  onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
                  placeholder="https://api.example.com/events"
                />
                <HelperText>HTTPS recommended. Your endpoint should respond 2xx within 10 seconds.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label>Description <MutedText as="span">(optional)</MutedText></Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Event types <MutedText as="span">(leave all unchecked to receive everything)</MutedText></Label>
                <EventTypesPicker
                  selected={form.event_types}
                  onChange={(v) => setForm(f => ({ ...f, event_types: v }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Payload format</Label>
                <Select
                  value={form.payload_version}
                  onChange={(e) => setForm(f => ({ ...f, payload_version: e.target.value as 'v1' | 'v2' }))}
                >
                  <option value="v2">v2 — current (recommended)</option>
                  <option value="v1">v1 — frozen legacy envelope</option>
                </Select>
                <HelperText>
                  v2 is the documented contract for new integrations (camelCase envelope,
                  door field, replay-proof signatures). Pick v1 only for an endpoint built
                  against the legacy format.
                </HelperText>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={closeNew}>Cancel</Button>
              <Button
                onClick={() => createMutation.mutate()}
                disabled={!form.name.trim() || !form.url.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating…' : 'Create'}
              </Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Edit webhook modal */}
      {editTarget && (
        <ModalOverlay onClick={closeEdit}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Edit Webhook — {editTarget.name}</H2Title>
              <ModalCloseButton onClick={closeEdit} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {updateMutation.error && (
                <ModalAlert variant="error">
                  {(updateMutation.error as any)?.response?.data?.error || 'Failed to update webhook.'}
                </ModalAlert>
              )}
              <FormGroup>
                <Label>Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>URL</Label>
                <Input
                  type="url"
                  value={editForm.url}
                  onChange={(e) => setEditForm(f => ({ ...f, url: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Description</Label>
                <Input
                  value={editForm.description}
                  onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Event types</Label>
                <EventTypesPicker
                  selected={editForm.event_types}
                  onChange={(v) => setEditForm(f => ({ ...f, event_types: v }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Payload format</Label>
                <Select
                  value={editForm.payload_version}
                  onChange={(e) => setEditForm(f => ({ ...f, payload_version: e.target.value as 'v1' | 'v2' }))}
                >
                  <option value="v2">v2 — current (recommended)</option>
                  <option value="v1">v1 — frozen legacy envelope</option>
                </Select>
                <HelperText>
                  Takes effect on the next delivery — update the endpoint's parser and
                  signature verification before switching. In-flight retries keep their
                  original format.
                </HelperText>
              </FormGroup>
              <FormGroup>
                <CheckLabel>
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm(f => ({ ...f, is_active: e.target.checked }))}
                  />
                  Active
                </CheckLabel>
                <HelperText>Pause a webhook to stop deliveries without deleting it.</HelperText>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={closeEdit}>Cancel</Button>
              <Button
                onClick={() => updateMutation.mutate()}
                disabled={!editForm.name.trim() || !editForm.url.trim() || updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Regenerate secret confirmation */}
      {regenTarget && (
        <ModalOverlay onClick={() => setRegenTarget(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Regenerate Secret</H2Title>
              <ModalCloseButton onClick={() => setRegenTarget(null)} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <p>
                Regenerate the HMAC secret for <strong>{regenTarget.name}</strong>? Deliveries will
                continue to sign with the new secret — any endpoint still validating against the old
                secret will start rejecting requests.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setRegenTarget(null)}>Cancel</Button>
              <Button
                onClick={() => regenMutation.mutate(regenTarget.id)}
                disabled={regenMutation.isPending}
              >
                {regenMutation.isPending ? 'Regenerating…' : 'Regenerate'}
              </Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <ModalOverlay onClick={() => setDeleteTarget(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Delete Webhook</H2Title>
              <ModalCloseButton onClick={() => setDeleteTarget(null)} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <p>
                Delete <strong>{deleteTarget.name}</strong>? All delivery history will be removed
                and no further events will be sent to this URL.
              </p>
              <SmallMutedTop>This cannot be undone.</SmallMutedTop>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button
                variant="danger"
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Secret reveal — shown once after create or regenerate */}
      {revealedSecret && (
        <ModalOverlay>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Webhook Secret — {revealedSecret.name}</H2Title>
              <ModalCloseButton onClick={() => setRevealedSecret(null)} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <KeyRevealBox>
                <KeyRevealLabel>HMAC Signing Secret</KeyRevealLabel>
                <KeyRevealValue>{revealedSecret.secret}</KeyRevealValue>
                <KeyRevealWarning>
                  Copy this secret now. It will not be shown again.
                </KeyRevealWarning>
              </KeyRevealBox>
              <BlockMuted>
                Use this secret to verify incoming webhook signatures. See Documentation &rarr; Webhooks
                for HMAC verification examples.
              </BlockMuted>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setRevealedSecret(null)}>Done</Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}
    </TopLevelDropdown>
  );
}

// ─── Testing section ─────────────────────────────────────────────────────────

type RecentFire = {
  id: string;
  eventType: string;
  firedAt: string;
  success: boolean;
  error?: string;
  channels?: string[];
  payloads?: {
    webhook: Record<string, unknown> | null;
    mqtt: Record<string, unknown>;
  };
  targets?: {
    webhooks: Array<{ id: string; name: string; url: string }>;
    mqtt: Array<{ id: number; appName: string; teamId: string; topic: string }>;
  };
};

// Programmatic equivalent of the Send button below — fires an access.granted
// synthetic event through every active webhook for the caller's company.
// isTest:true in the wire payload so production handlers can filter it out.
const TEST_EVENT_CURL_EXAMPLE = `curl -X POST https://api.simkura.com/api/v1/test-events \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"eventType":"access.granted"}'`;

// One row per webhook event the tester can fire. `description` covers what
// triggers the event on a real device and what the `data` block carries.
type TestEventDef = { type: string; label: string; description: string };

const TEST_EVENT_GROUPS: Array<{ label: string; events: TestEventDef[] }> = [
  {
    label: 'Device lifecycle',
    events: [
      {
        type: 'device.wake', label: 'Wake',
        description: 'The device woke from sleep — on its reporting cadence or from reader '
          + 'activity — to report state and pick up queued commands. Data: wakeUpCount, '
          + 'sleepDurationMs.',
      },
      {
        type: 'device.sleep', label: 'Sleep',
        description: 'The device finished its wake work and returned to sleep. '
          + 'Data: awakeDurationMs.',
      },
      {
        type: 'device.restart', label: 'Restart',
        description: 'The device rebooted — power-on, a commanded reboot, or a factory reset. '
          + 'Data: reason, reasonCode, brownoutCount.',
      },
      {
        type: 'device.reconnect', label: 'Reconnect',
        description: 'The device re-established its cellular/MQTT connection after dropping it. '
          + 'Data: code.',
      },
      {
        type: 'device.online', label: 'Online',
        description: 'The device was seen again after being offline — emitted on the first '
          + 'report after a gap. Data: none (the envelope timestamp says when).',
      },
      {
        type: 'device.offline', label: 'Offline',
        description: 'The device missed its expected reporting window — time-based, detected by '
          + 'the platform sweep rather than sent by the device. Data: lastSeen.',
      },
      {
        type: 'device.deployed', label: 'Deployed',
        description: 'The device was marked deployed (live/billable) from the dashboard. '
          + 'Data: deployedAt, deploymentBatch.',
      },
      {
        type: 'device.undeployed', label: 'Undeployed',
        description: 'The device was taken out of deployment from the dashboard. '
          + 'Data: deploymentBatch (preserved as the historical record).',
      },
    ],
  },
  {
    label: 'Health',
    events: [
      {
        type: 'health.battery_low', label: 'Battery low',
        description: 'The reported battery level dropped into the low bucket — plan a battery '
          + 'change. Data: batteryPct.',
      },
      {
        type: 'health.battery_dead', label: 'Battery dead',
        description: 'The battery reached the dead bucket and the device entered safe mode. '
          + 'Data: batteryPct.',
      },
      {
        type: 'health.battery_recovered', label: 'Battery recovered',
        description: 'The battery returned to a healthy level from an alarm state — usually a '
          + 'battery swap. Data: batteryPct, swapped.',
      },
      {
        type: 'health.reader_wedged', label: 'Reader wedged',
        description: 'The reader input buffer filled repeatedly — the reader is likely wedged '
          + 'or being held. Data: bufferFullCount, door.',
      },
      {
        type: 'health.recovery_boot', label: 'Recovery boot',
        description: 'The firmware escalated to a cold reboot to recover itself (watchdog '
          + 'auto-recovery) — a health alarm, unlike a routine restart. Data: reason, '
          + 'brownoutCount.',
      },
    ],
  },
  {
    label: 'Access',
    events: [
      {
        type: 'access.granted', label: 'Granted',
        description: 'A valid credential was presented at the reader and the door unlocked. '
          + 'Data: credential (cardNumber, facilityCode — or pin shape), timestamp.',
      },
      {
        type: 'access.denied', label: 'Denied',
        description: 'An unknown or blocked credential was presented and rejected. '
          + 'Data: credential, reason, timestamp.',
      },
    ],
  },
  {
    label: 'Door state',
    events: [
      {
        type: 'lock.state_changed', label: 'Lock state changed',
        description: 'The lock transitioned state — a shift schedule kicked in, a command '
          + 'landed, or an override/lockdown was applied. Data: lockState, override, trigger, '
          + 'code.',
      },
    ],
  },
  {
    label: 'Command lifecycle',
    events: [
      {
        type: 'command.sent', label: 'Sent',
        description: 'A queued command was delivered to the device on wake. Data: command, code.',
      },
      {
        type: 'command.failed', label: 'Failed',
        description: 'A queued command failed at the gateway or in delivery. Data: command, '
          + 'error, source.',
      },
    ],
  },
];

// Events the MQTT bridge emits itself when accepting / rejecting a
// customer-published command. MQTT-only — webhooks never deliver these.
const MQTT_ONLY_EVENTS: TestEventDef[] = [
  {
    type: 'command.queued', label: 'Queued',
    description: 'The bridge accepted a command you published on the outbound topic and '
      + 'queued it for delivery. Emitted by the bridge itself with a top-level envelope '
      + '(no data wrapper). Data: command.',
  },
  {
    type: 'command.failed.bridge', label: 'Failed (bridge)',
    description: 'The bridge rejected a command you published — wire event type is '
      + 'command.failed with a top-level envelope, so parsers see a second shape for the '
      + 'same type. Data: command, error.',
  },
];

type MqttPublishResult = {
  firedAt: string;
  success: boolean;
  error?: string;
  deviceId?: string;
  payload?: Record<string, unknown>;
  targets?: { mqtt: Array<{ id: number; appName: string; teamId: string; topic: string }> };
};

const TestDeviceBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin: ${({ theme }) => theme.spacing(3, 0, 4, 0)};
  padding: ${({ theme }) => theme.spacing(3, 4)};
  background: ${({ theme }) => theme.background.secondary};
  border-radius: ${({ theme }) => theme.border.radius.md};
`;

const PayloadBlock = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.background.secondary};
  border-radius: ${({ theme }) => theme.border.radius.md};
`;

const PayloadHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const PayloadJson = styled.pre`
  margin: 0;
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.background.primary};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-family: ${({ theme }) => theme.font.familyMono || 'monospace'};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.primary};
  overflow-x: auto;
  white-space: pre;
`;

const TargetsBlock = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const TargetList = styled.ul`
  margin: ${({ theme }) => theme.spacing(1, 0, 0, 0)};
  padding-left: ${({ theme }) => theme.spacing(4)};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.secondary};
`;

const TargetListItem = styled.li`
  padding: ${({ theme }) => theme.spacing(0.5, 0)};
`;

const RecentBlock = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};
  padding-top: ${({ theme }) => theme.spacing(4)};
  border-top: 1px solid ${({ theme }) => theme.border.color.light};
`;

const RecentItemRow = styled.div<{ $success: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1, 0)};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme, $success }) =>
    $success ? theme.font.color.primary : theme.font.color.danger};
`;

const SubsectionDivider = styled.div`
  margin-top: ${({ theme }) => theme.spacing(6)};
  padding-top: ${({ theme }) => theme.spacing(5)};
  border-top: 1px solid ${({ theme }) => theme.border.color.medium};
`;

const HowItWorksBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const HowItWorksPanel = styled.div`
  padding: ${({ theme }) => theme.spacing(3, 4)};
  background: ${({ theme }) => theme.background.secondary};
  border-left: 3px solid ${({ theme }) => theme.accent.primary};
  border-radius: ${({ theme }) => theme.border.radius.sm};
`;

const HowItWorksLabel = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const HowItWorksBody = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.5;
`;

const SubsectionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(2)} 0;
  font-size: ${({ theme }) => theme.font.size.md};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
`;

// ── Virtual devices section ────────────────────────────────────────────────

const VirtualDevicePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.background.secondary};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  border-radius: ${({ theme }) => theme.border.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const VirtualDevicePanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const VirtualDevicePanelTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.font.size.md};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
`;

const StatusPill = styled.span<{ $variant: 'online' | 'offline' }>`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing(0.5, 2)};
  border-radius: 999px;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: ${({ $variant }) =>
    $variant === 'online' ? '#10B9811A' : '#EF44441A'};
  color: ${({ $variant }) =>
    $variant === 'online' ? '#059669' : '#B91C1C'};
`;

const RoutesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const RouteField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const RouteFieldLabel = styled.div`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.font.color.tertiary};
`;

const RouteCode = styled.code`
  display: block;
  padding: ${({ theme }) => theme.spacing(2, 3)};
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-family: ${({ theme }) => theme.font.familyMono || 'monospace'};
  font-size: 12px;
  color: ${({ theme }) => theme.font.color.primary};
  word-break: break-all;
`;

function VirtualDevices({
  online,
  offline,
}: {
  online: string;
  offline: string;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  function renderRow(
    id: string,
    label: string,
    variant: 'online' | 'offline',
    deviceId: string,
  ) {
    const open = expandedId === id;
    return (
      <EndpointRow key={id}>
        <EndpointHeader
          onClick={() => setExpandedId(open ? null : id)}
          aria-expanded={open}
        >
          <StatusPill $variant={variant}>{variant}</StatusPill>
          <AccordionTitle>{label}</AccordionTitle>
          <ExpandCaret $open={open}>▶</ExpandCaret>
        </EndpointHeader>
        {open && (
          <EndpointBody>
            <RoutesGrid>
              <RouteField>
                <RouteFieldLabel>Device ID</RouteFieldLabel>
                <RouteCode>{deviceId}</RouteCode>
              </RouteField>

              <RouteField>
                <RouteFieldLabel>REST v2 &mdash; capability-model resource</RouteFieldLabel>
                <RouteCode>GET /v2/devices/{deviceId}</RouteCode>
              </RouteField>

              <RouteField>
                <RouteFieldLabel>REST v1 (legacy) &mdash; state snapshot</RouteFieldLabel>
                <RouteCode>GET /v1/devices/{deviceId}/state</RouteCode>
              </RouteField>
            </RoutesGrid>
          </EndpointBody>
        )}
      </EndpointRow>
    );
  }

  // Rendered inside the REST API section — no top-level wrapper of its own.
  return (
    <>
      <SectionBlurbLg>
        Two globally-fixed device IDs you can read at any time &mdash; one healthy and one
        unhealthy. Both REST endpoints always return a fresh snapshot, so you can build and
        test your polling and parsing before a real device is deployed.
      </SectionBlurbLg>

      <EndpointList>
        {renderRow('online',  'Healthy device',   'online',  online)}
        {renderRow('offline', 'Unhealthy device', 'offline', offline)}
      </EndpointList>
    </>
  );
}

type ResourceFetch = {
  fetchedAt: string;
  success: boolean;
  error?: string;
  endpoint: string;
  payload?: Record<string, unknown>;
};

// One button per v2 REST read the tester can exercise. `description` tells
// the user what populates that data in real life and what the response carries.
type ResourceTarget = {
  label: string;
  endpoint: string;
  description: string;
  run: () => Promise<Record<string, unknown>>;
};

const ResourceRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const ResourceRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(4)};
  align-items: flex-start;
`;

const ResourceButtonCell = styled.div`
  flex: 0 0 170px;

  button {
    width: 100%;
  }
`;

const ResourceDesc = styled.div`
  flex: 1;
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: ${({ theme }) => theme.font.lineHeight.lg};

  code {
    display: inline-block;
    margin-bottom: ${({ theme }) => theme.spacing(1)};
  }
`;

export function Testing({ companyId }: { companyId: string }) {
  // One category accordion open at a time; first group open by default.
  const [openGroup, setOpenGroup] = useState<string | null>(TEST_EVENT_GROUPS[0].label);
  const [lastFire, setLastFire] = useState<RecentFire | null>(null);
  const [history, setHistory] = useState<RecentFire[]>([]);

  const { data: info } = useQuery({
    queryKey: ['dev-test-event-info', companyId],
    queryFn: () => getTestEventInfo(companyId),
  });

  const fireMutation = useMutation({
    mutationFn: (type: string) => fireTestEvent(type, companyId),
    onSuccess: (result) => {
      const entry: RecentFire = {
        id: result.eventId,
        eventType: result.eventType,
        firedAt: new Date().toISOString(),
        success: true,
        channels: result.channels,
        payloads: result.payloads,
        targets: result.targets,
      };
      if (lastFire) setHistory((h) => [lastFire, ...h].slice(0, 6));
      setLastFire(entry);
    },
    onError: (err: any, type) => {
      const entry: RecentFire = {
        id: `err-${Date.now()}`,
        eventType: type,
        firedAt: new Date().toISOString(),
        success: false,
        error: err?.response?.data?.error || err?.message || 'Failed',
      };
      if (lastFire) setHistory((h) => [lastFire, ...h].slice(0, 6));
      setLastFire(entry);
    },
  });

  const [lastResource, setLastResource] = useState<ResourceFetch | null>(null);

  const resourceMutation = useMutation({
    mutationFn: (target: ResourceTarget) => target.run(),
    onSuccess: (payload, target) => {
      setLastResource({
        fetchedAt: new Date().toISOString(),
        success: true,
        endpoint: target.endpoint,
        payload,
      });
    },
    onError: (err: any, target) => {
      setLastResource({
        fetchedAt: new Date().toISOString(),
        success: false,
        endpoint: target.endpoint,
        error: err?.response?.data?.error || err?.message || 'Failed',
      });
    },
  });

  const resourceTargets: ResourceTarget[] = info ? [
    {
      label: 'Device list',
      endpoint: 'GET /api/v2/devices',
      description: 'Your real fleet. Devices appear here when Simkura provisions them to your '
        + 'company, and their status updates as they report in. Returns a paginated list of '
        + 'spine records — meta (status, deployed, lastSeen), device identity (id, board, '
        + 'firmware, numDoors), and the capability list per device. Test and virtual devices '
        + 'are not included.',
      run: () => getV2Devices(10),
    },
    {
      label: 'Test device',
      endpoint: `GET /api/v2/devices/${info.deviceId}`,
      description: 'The synthetic device the Send button above fires events from. It has no '
        + 'hardware behind it, so the resource is a stable sample. Returns the full '
        + 'capability-model resource — doors[] with lock state, reader, and credential counts, '
        + 'plus power (state, batteryPct, batteryHealth) and connectivity (carrier, signal).',
      run: () => getV2DeviceResource(info.deviceId),
    },
    {
      label: 'Healthy device',
      endpoint: `GET /api/v2/devices/${info.virtualDevices.online}`,
      description: 'A globally-fixed fixture that always reports a steady healthy state: online, '
        + 'sleeping between wakes, 95% battery, strong signal. Same full resource shape as a '
        + 'real device — use it to build your happy-path parsing before hardware is deployed.',
      run: () => getV2DeviceResource(info.virtualDevices.online),
    },
    {
      label: 'Unhealthy device',
      endpoint: `GET /api/v2/devices/${info.virtualDevices.offline}`,
      description: 'The failure-mode counterpart: offline with lastSeen hours stale, deep sleep, '
        + '12% battery with batteryHealth "low", weak signal. Same resource shape — use it to '
        + 'verify your alerting and edge-case handling against realistic alarm values.',
      run: () => getV2DeviceResource(info.virtualDevices.offline),
    },
    {
      label: 'Command queue',
      endpoint: `GET /api/v2/devices/${info.deviceId}/commands`,
      description: 'Populated when you queue commands (e.g. POST /v2/devices/{id}/doors/1/unlock) '
        + 'and drained as the gateway delivers them on device wake. Returns command records — '
        + 'id, operation, door, status (queued/sending/sent/failed/cancelled/expired), createdAt, '
        + 'expiresAt. The test device takes no commands, so its queue is always empty.',
      run: () => getV2DeviceCommands(info.deviceId),
    },
  ] : [];

  // ── MQTT section state ──
  const [lastState, setLastState] = useState<MqttPublishResult | null>(null);
  const stateMutation = useMutation({
    mutationFn: () => fireTestStateUpdate(companyId),
    onSuccess: (result) => setLastState({
      firedAt: new Date().toISOString(),
      success: true,
      deviceId: result.deviceId,
      payload: result.payload,
      targets: result.targets,
    }),
    onError: (err: any) => setLastState({
      firedAt: new Date().toISOString(),
      success: false,
      error: err?.response?.data?.error || err?.message || 'Failed',
    }),
  });

  const [lastSnapshot, setLastSnapshot] = useState<{
    success: boolean;
    integrationCount: number;
    error?: string;
  } | null>(null);
  const snapshotMutation = useMutation({
    mutationFn: () => publishVirtualSnapshots(companyId),
    onSuccess: (result) => setLastSnapshot({
      success: true,
      integrationCount: result.devices[0]?.topics.length ?? 0,
    }),
    onError: (err: any) => setLastSnapshot({
      success: false,
      integrationCount: 0,
      error: err?.response?.data?.error || err?.message || 'Failed',
    }),
  });

  const [mqttLastFire, setMqttLastFire] = useState<RecentFire | null>(null);
  const mqttFireMutation = useMutation({
    mutationFn: (type: string) => fireTestEvent(type, companyId),
    onSuccess: (result) => setMqttLastFire({
      id: result.eventId,
      eventType: result.eventType,
      firedAt: new Date().toISOString(),
      success: true,
      channels: result.channels,
      payloads: result.payloads,
      targets: result.targets,
    }),
    onError: (err: any, type) => setMqttLastFire({
      id: `err-${Date.now()}`,
      eventType: type,
      firedAt: new Date().toISOString(),
      success: false,
      error: err?.response?.data?.error || err?.message || 'Failed',
    }),
  });

  return (
    <>
    {/* ── 1 · Webhooks ─────────────────────────────────────────────────── */}
    <TopLevelDropdown title="Webhooks" subtitle="Fire synthetic events at your endpoints" defaultOpen>

      <SectionBlurbLg>
        Fire synthetic events from the test device to verify your webhook handling
        end-to-end &mdash; real filtering, signing, delivery, and retries. Each event is
        marked with <InlineCode>isTest: true</InlineCode> at the top level so your
        production handlers can filter it out.
      </SectionBlurbLg>

      <TestDeviceBlock>
        <MicroLabel>Test device ID</MicroLabel>
        <InlineCode>{info?.deviceId || '…'}</InlineCode>
      </TestDeviceBlock>

      <div style={{ marginTop: 24 }}>
        <SubsectionLabel>Fire a test event from your terminal</SubsectionLabel>
        <JsonBlock>{TEST_EVENT_CURL_EXAMPLE}</JsonBlock>
      </div>

      <EndpointList>
        {TEST_EVENT_GROUPS.map((group) => {
          const open = openGroup === group.label;
          return (
            <EndpointRow key={group.label}>
              <EndpointHeader
                onClick={() => setOpenGroup(open ? null : group.label)}
                aria-expanded={open}
              >
                <AccordionTitle>{group.label}</AccordionTitle>
                <MutedText>{group.events.length} event{group.events.length !== 1 ? 's' : ''}</MutedText>
                <ExpandCaret $open={open}>▶</ExpandCaret>
              </EndpointHeader>
              {open && (
                <EndpointBody>
                  <ResourceRows>
                    {group.events.map((e) => (
                      <ResourceRow key={e.type}>
                        <ResourceButtonCell>
                          <Button
                            variant="secondary"
                            onClick={() => fireMutation.mutate(e.type)}
                            disabled={fireMutation.isPending}
                          >
                            {e.label}
                          </Button>
                        </ResourceButtonCell>
                        <ResourceDesc>
                          <InlineCode>{e.type}</InlineCode>
                          <div>{e.description}</div>
                        </ResourceDesc>
                      </ResourceRow>
                    ))}
                  </ResourceRows>
                </EndpointBody>
              )}
            </EndpointRow>
          );
        })}
      </EndpointList>

      {lastFire && (
        <PayloadBlock>
          <PayloadHeader>
            <span>{lastFire.success ? '✓' : '✗'}</span>
            <InlineCode>{lastFire.eventType}</InlineCode>
            <MutedText>{timeAgo(lastFire.firedAt)}</MutedText>
            {lastFire.error && <MutedText>— {lastFire.error}</MutedText>}
          </PayloadHeader>

          {lastFire.payloads && (
            <>
              <TargetsBlock>
                <MicroLabel>
                  {lastFire.targets && lastFire.targets.webhooks.length > 0
                    ? `Sent to ${lastFire.targets.webhooks.length} webhook${lastFire.targets.webhooks.length > 1 ? 's' : ''}`
                    : 'No matching webhooks'}
                </MicroLabel>
                {lastFire.targets && lastFire.targets.webhooks.length > 0 ? (
                  <TargetList>
                    {lastFire.targets.webhooks.map((w) => (
                      <TargetListItem key={w.id}>
                        {w.name} &mdash; <InlineCode>{w.url}</InlineCode>
                      </TargetListItem>
                    ))}
                  </TargetList>
                ) : (
                  <BlockMuted>
                    No active webhook is registered for events of this type. Configure one
                    in the Webhooks tab to receive these.
                  </BlockMuted>
                )}
              </TargetsBlock>

              <MicroLabel>Delivered webhook payload</MicroLabel>
              <PayloadJson>
                {JSON.stringify(lastFire.payloads.webhook, null, 2)}
              </PayloadJson>
            </>
          )}
        </PayloadBlock>
      )}

      {history.length > 0 && (
        <RecentBlock>
          <MicroLabel>Earlier fires</MicroLabel>
          <Stack gap={1}>
            {history.map((r) => (
              <RecentItemRow key={r.id} $success={r.success}>
                <span>{r.success ? '✓' : '✗'}</span>
                <InlineCode>{r.eventType}</InlineCode>
                <MutedText>{timeAgo(r.firedAt)}</MutedText>
                {r.error && <MutedText>— {r.error}</MutedText>}
              </RecentItemRow>
            ))}
          </Stack>
        </RecentBlock>
      )}

    </TopLevelDropdown>

    {/* ── 2 · REST API ─────────────────────────────────────────────────── */}
    <TopLevelDropdown title="REST API" subtitle="Read-only v2 responses">
        <SectionBlurbLg>
          Events tell you <strong>what happened</strong>; these reads tell you
          <strong> what is</strong>. Everything here is <strong>read-only</strong> (scope
          <InlineCode> devices:read</InlineCode>) — one button per v2 read response, and
          nothing changes device state. Command <em>writes</em> use the door-scoped
          <InlineCode> /doors/:door/…</InlineCode> endpoints (scope
          <InlineCode> devices:commands</InlineCode>) and aren&apos;t fired from here.
          Each button shows the live response below.
        </SectionBlurbLg>

        <ResourceRows>
          {resourceTargets.map((target) => (
            <ResourceRow key={target.label}>
              <ResourceButtonCell>
                <Button
                  variant="secondary"
                  onClick={() => resourceMutation.mutate(target)}
                  disabled={resourceMutation.isPending}
                >
                  {target.label}
                </Button>
              </ResourceButtonCell>
              <ResourceDesc>
                <InlineCode>{target.endpoint}</InlineCode>
                <div>{target.description}</div>
              </ResourceDesc>
            </ResourceRow>
          ))}
        </ResourceRows>

        {lastResource && (
          <PayloadBlock>
            <PayloadHeader>
              <span>{lastResource.success ? '✓' : '✗'}</span>
              <InlineCode>{lastResource.endpoint}</InlineCode>
              <MutedText>{timeAgo(lastResource.fetchedAt)}</MutedText>
              {lastResource.error && <MutedText>— {lastResource.error}</MutedText>}
            </PayloadHeader>

            {lastResource.payload && (
              <PayloadJson>
                {JSON.stringify(lastResource.payload, null, 2)}
              </PayloadJson>
            )}
          </PayloadBlock>
        )}

        <SubsectionDivider>
          <SubsectionTitle>Virtual devices</SubsectionTitle>
          {info?.virtualDevices && (
            <VirtualDevices
              online={info.virtualDevices.online}
              offline={info.virtualDevices.offline}
            />
          )}
        </SubsectionDivider>
    </TopLevelDropdown>

    {/* ── 3 · MQTT ─────────────────────────────────────────────────────── */}
    <TopLevelDropdown title="MQTT" subtitle="For MQTT bridge integrations">
        <SectionBlurbLg>
          Every event fired in the Webhooks section also publishes to your active MQTT
          integrations on the <InlineCode>…/events</InlineCode> topic. This section covers
          what&apos;s MQTT-specific: the <InlineCode>/state</InlineCode> topic and the events
          the bridge emits itself. Integrations are managed in the MQTT tab.
        </SectionBlurbLg>

        <SubsectionLabel>State topic</SubsectionLabel>
        <SectionBlurb>
          Publishes a test state snapshot for the test device on <InlineCode>/state</InlineCode> —
          subscribe first, then press to watch it arrive.
        </SectionBlurb>
        <Button
          onClick={() => stateMutation.mutate()}
          disabled={stateMutation.isPending}
        >
          {stateMutation.isPending ? 'Publishing…' : 'Publish test state update'}
        </Button>

        {lastState && (
          <PayloadBlock>
            <PayloadHeader>
              <span>{lastState.success ? '✓' : '✗'}</span>
              <InlineCode>{lastState.deviceId || 'state'}</InlineCode>
              <MutedText>{timeAgo(lastState.firedAt)}</MutedText>
              {lastState.error && <MutedText>— {lastState.error}</MutedText>}
            </PayloadHeader>

            {lastState.payload && (
              <>
                <TargetsBlock>
                  <MicroLabel>
                    {lastState.targets && lastState.targets.mqtt.length > 0
                      ? `Published on ${lastState.targets.mqtt.length} MQTT integration${lastState.targets.mqtt.length > 1 ? 's' : ''}`
                      : 'No active MQTT integrations'}
                  </MicroLabel>
                  {lastState.targets && lastState.targets.mqtt.length > 0 ? (
                    <TargetList>
                      {lastState.targets.mqtt.map((m) => (
                        <TargetListItem key={m.id}>
                          {m.appName} &mdash; <InlineCode>{m.topic}</InlineCode>
                        </TargetListItem>
                      ))}
                    </TargetList>
                  ) : (
                    <BlockMuted>
                      No active MQTT integration is configured. Set one up in the MQTT tab
                      to receive this.
                    </BlockMuted>
                  )}
                </TargetsBlock>

                <PayloadJson>
                  {JSON.stringify(lastState.payload, null, 2)}
                </PayloadJson>
              </>
            )}
          </PayloadBlock>
        )}

        <SubsectionDivider>
          <SubsectionLabel>Virtual device snapshots</SubsectionLabel>
          <SectionBlurb>
            Retains both virtual-device snapshots on your bridge so a subscriber connecting
            now receives them immediately (REST reads of the virtual devices always work
            without this).
          </SectionBlurb>
          <Button
            onClick={() => snapshotMutation.mutate()}
            disabled={snapshotMutation.isPending}
          >
            {snapshotMutation.isPending ? 'Publishing…' : 'Publish snapshots to MQTT'}
          </Button>
          {lastSnapshot && (
            <BlockMuted>
              {lastSnapshot.success
                ? lastSnapshot.integrationCount > 0
                  ? `Published to ${lastSnapshot.integrationCount} MQTT integration${lastSnapshot.integrationCount === 1 ? '' : 's'}.`
                  : 'No active MQTT integrations configured — REST reads still work on demand.'
                : `Failed: ${lastSnapshot.error}`}
            </BlockMuted>
          )}
        </SubsectionDivider>

        <SubsectionDivider>
          <SubsectionLabel>Bridge-emitted events</SubsectionLabel>
          <SectionBlurb>
            MQTT-only — these come from the bridge accepting or rejecting a command you
            publish, so webhooks never deliver them.
          </SectionBlurb>
          <ResourceRows>
            {MQTT_ONLY_EVENTS.map((e) => (
              <ResourceRow key={e.type}>
                <ResourceButtonCell>
                  <Button
                    variant="secondary"
                    onClick={() => mqttFireMutation.mutate(e.type)}
                    disabled={mqttFireMutation.isPending}
                  >
                    {e.label}
                  </Button>
                </ResourceButtonCell>
                <ResourceDesc>
                  <InlineCode>{e.type}</InlineCode>
                  <div>{e.description}</div>
                </ResourceDesc>
              </ResourceRow>
            ))}
          </ResourceRows>

          {mqttLastFire && (
            <PayloadBlock>
              <PayloadHeader>
                <span>{mqttLastFire.success ? '✓' : '✗'}</span>
                <InlineCode>{mqttLastFire.eventType}</InlineCode>
                <MutedText>{timeAgo(mqttLastFire.firedAt)}</MutedText>
                {mqttLastFire.error && <MutedText>— {mqttLastFire.error}</MutedText>}
              </PayloadHeader>

              {mqttLastFire.payloads && (
                <>
                  <TargetsBlock>
                    <MicroLabel>
                      {mqttLastFire.targets && mqttLastFire.targets.mqtt.length > 0
                        ? `Published on ${mqttLastFire.targets.mqtt.length} MQTT integration${mqttLastFire.targets.mqtt.length > 1 ? 's' : ''}`
                        : 'No active MQTT integrations'}
                    </MicroLabel>
                    {mqttLastFire.targets && mqttLastFire.targets.mqtt.length > 0 ? (
                      <TargetList>
                        {mqttLastFire.targets.mqtt.map((m) => (
                          <TargetListItem key={m.id}>
                            {m.appName} &mdash; <InlineCode>{m.topic}</InlineCode>
                          </TargetListItem>
                        ))}
                      </TargetList>
                    ) : (
                      <BlockMuted>
                        No active MQTT integration is configured. Set one up in the MQTT tab
                        to subscribe to these.
                      </BlockMuted>
                    )}
                  </TargetsBlock>

                  <MicroLabel>Published MQTT payload</MicroLabel>
                  <PayloadJson>
                    {JSON.stringify(mqttLastFire.payloads.mqtt, null, 2)}
                  </PayloadJson>
                </>
              )}
            </PayloadBlock>
          )}
        </SubsectionDivider>
    </TopLevelDropdown>
    </>
  );
}
